// @owners { team: patients-team }
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { useNextAvailableDateAddressId } from '~shared/features/next-available-date/hooks/useNextAvailableDateAddressId';
import { fetchCheckoutNextAvailableDates } from '~shared/features/next-available-date/queries/fetchCheckoutNextAvailableDates';
import { getNextAvailableDateFacilityID } from '~shared/features/next-available-date/selectors/getNextAvailableDateFacilityId';
import getShipmentID from '~shared/features/next-available-date/selectors/getShipmentId';
import { useSelectorShared } from '~shared/store';

export const useFetchNextAvailableDatesForCart = () => {
  const queryClient = useQueryClient();
  const { prescriptionIDs } = usePrescriptionsInCart();
  const addressID = useNextAvailableDateAddressId();
  const facilityID = useSelectorShared(getNextAvailableDateFacilityID);
  const shipmentID = useSelectorShared(getShipmentID);

  return useCallback(
    async (options?: { unbundledPrescriptionIDs?: number[]; context?: string }) => {
      const unbundledPrescriptionIDs = options?.unbundledPrescriptionIDs || [];
      // on app start do not fetch next available dates when no prescriptions are in the cart
      if (unbundledPrescriptionIDs.length === 0 && prescriptionIDs.length === 0) {
        return;
      }

      await fetchCheckoutNextAvailableDates(queryClient, {
        unbundledPrescriptionIDs,
        bundledPrescriptionIDs: prescriptionIDs,
        addressID,
        facilityID,
        shipmentID,
      });
    },
    [addressID, facilityID, prescriptionIDs, queryClient, shipmentID],
  );
};
